import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FirestoreDB } from '../../../utils/firebase/firebase';
import { getSessionNotesPathInfo } from '../utils/sessionFirestoreHelpers';
import { createClinicalSessionParams } from '../createClinicalSessionParams';
import { handleSubmitSession } from '../utils/submitSessionUtil';
import { safeJsonBtoa } from '../../../utils/encodingUtils';


export const useClinicalSessionSaveHandlers = ({
    sessionId,
    sessionData,
    session,
    user,
    typeOfUser,
    preRegisteredUsers,
    currentUser,
    goalsDataEntryRef,
    quickTracksDataEntryRef,
    goalsDataCardRef,
    quickTracksDataCardRef,
    updateSessionData,
    isSavingSessionNotes,
    setIsSavingSessionNotes,
    setIsSaving,
    clearSessionData,
    closeSession,
    maximizeSession,
    studentGoalsData,
    studentBehaviorData,
}) => {
    const queryClient = useQueryClient();

    const handleSaveDraft = useCallback(async () => {
        if (isSavingSessionNotes) {
            return;
        }

        try {
            const isEditMode = Boolean(sessionData?.editingSessionId);
            const targetSessionNoteId = isEditMode
                ? sessionData.editingSessionId
                : sessionId;

            if (!targetSessionNoteId) {
                throw new Error('Missing session identifier for draft save.');
            }

            const { studentId, collectionName } = getSessionNotesPathInfo(
                sessionData,
                user,
                typeOfUser,
                preRegisteredUsers
            );

            setIsSavingSessionNotes(true);

            const sessionDocRef = doc(
                FirestoreDB,
                collectionName,
                studentId,
                'sessionNotes',
                targetSessionNoteId
            );

            const goalsArray = (sessionData.selectedGoals || []).map((goalId) => ({
                goalID: goalId,
                dataEntryID: goalsDataEntryRef?.current?.[goalId] || null,
            }));

            const quickTracksArray = (sessionData.selectedQuickTracks || []).map(
                (trackId) => ({
                    quickTrackID: trackId,
                    dataEntryID: quickTracksDataEntryRef?.current?.[trackId] || null,
                })
            );

            const sessionNotesObject = createClinicalSessionParams(
                sessionData,
                goalsArray,
                quickTracksArray,
                currentUser,
                sessionData
            );
            const clonedGoalDrafts = sessionData?.goalEntryDrafts
                ? JSON.parse(JSON.stringify(sessionData.goalEntryDrafts))
                : {};
            const clonedQuickTrackDrafts = sessionData?.quickTrackDrafts
                ? JSON.parse(JSON.stringify(sessionData.quickTrackDrafts))
                : {};
            const sessionNotesPayload = {
                ...sessionNotesObject,
                ...(Object.keys(clonedGoalDrafts).length
                    ? { goalEntryDrafts: clonedGoalDrafts }
                    : {}),
                ...(Object.keys(clonedQuickTrackDrafts).length
                    ? { quickTrackDrafts: clonedQuickTrackDrafts }
                    : {}),
                status: 'draft',
                sessionStatus: 'draft',
                isDraft: true,
            };

            const encodedInfo = safeJsonBtoa(sessionNotesPayload);

            const now = Timestamp.now();

            const nowIsoString = now.toDate().toISOString();
            const draftPayload = {
                sessionId: targetSessionNoteId,
                studentId,
                info: encodedInfo,
                createdAt: session?.originalData?.createdAt || now,
                updatedAt: now,
                isEncrypted: false,
                isDraft: true,
                status: 'draft',
                updatedBy: currentUser?.uid || null,
            };

            await setDoc(sessionDocRef, draftPayload);
            updateSessionData(sessionId, { lastSavedAt: nowIsoString });

            // Invalidate and refetch session notes cache to ensure fresh data is displayed
            if (studentId) {
                await queryClient.invalidateQueries({
                    queryKey: ['sessionNotes', currentUser?.uid, typeOfUser, studentId],
                    refetchType: 'all',
                });
                // Explicitly refetch to ensure immediate update
                await queryClient.refetchQueries({
                    queryKey: ['sessionNotes', currentUser?.uid, typeOfUser, studentId],
                });
            }

            toast.success('Draft saved successfully.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        } catch (error) {
            console.error('Error saving session draft:', error);
            toast.error(error.message || 'Failed to save draft.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        } finally {
            setIsSavingSessionNotes(false);
        }
    }, [
        sessionId,
        sessionData,
        session,
        user,
        typeOfUser,
        preRegisteredUsers,
        currentUser,
        goalsDataEntryRef,
        quickTracksDataEntryRef,
        updateSessionData,
        queryClient,
        isSavingSessionNotes,
        setIsSavingSessionNotes,
    ]);

    const handleSaveSession = useCallback(async () => {
        if (isSavingSessionNotes) {
            return;
        }

        // When session is not attended, skip data entry component saves
        const isSessionNotAttended = sessionData.sessionAttended === 'No';

        // Capture the save functions IMMEDIATELY to prevent losing them during re-renders
        const goalSaveAll =
            goalsDataCardRef.current && goalsDataCardRef.current?.saveAll;
        const quickTrackSaveAll =
            quickTracksDataCardRef.current && quickTracksDataCardRef.current?.saveAll;

        // Validate we have the save functions before proceeding (only if session is attended)
        if (!isSessionNotAttended && (!goalSaveAll || !quickTrackSaveAll)) {
            toast.error('Unable to save: data entry components not ready', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        try {
            // Set saving state ONLY for UI feedback (button disabling)
            // Do this before the actual save operations to prevent double-clicks
            setIsSavingSessionNotes(true);
            setIsSaving(sessionId, true);

            // Only save data entry components if session is attended
            if (!isSessionNotAttended) {
                // Call saveAll on GoalsDataCard to save all goal entries
                if (goalSaveAll) {
                    const goalResults = await goalSaveAll();
                    // Update the goalsDataEntryRef with the captured IDs
                    goalResults.forEach((result) => {
                        if (result.dataEntryId) {
                            goalsDataEntryRef.current[result.goalId] = result.dataEntryId;
                        }
                    });
                }

                // Call saveAll on QuickTracksDataCard to save all quick track entries
                if (quickTrackSaveAll) {
                    const quickTrackResults = await quickTrackSaveAll();
                    // Update the quickTracksDataEntryRef with the captured IDs
                    quickTrackResults.forEach((result) => {
                        if (result.dataEntryId) {
                            quickTracksDataEntryRef.current[result.behaviorId] =
                                result.dataEntryId;
                        }
                    });
                }

                // Verify all required IDs are present (only if session is attended)
                const goalIds = sessionData.selectedGoals || [];
                const quickTrackIds = sessionData.selectedQuickTracks || [];

                const missingGoalIds = goalIds.filter(
                    (goalId) => !goalsDataEntryRef.current?.[goalId]
                );
                const missingQuickTrackIds = quickTrackIds.filter(
                    (trackId) => !quickTracksDataEntryRef.current?.[trackId]
                );

                if (missingGoalIds.length || missingQuickTrackIds.length) {
                    throw new Error(
                        'Some data entries are missing IDs. Ensure each goal and quick track entry is saved before submitting the session.'
                    );
                }
            }

            // Submit the session
            await handleSubmitSession(
                session,
                sessionData,
                sessionId,
                user,
                goalsDataEntryRef,
                quickTracksDataEntryRef,
                currentUser,
                typeOfUser,
                preRegisteredUsers,
                studentGoalsData,
                studentBehaviorData
            );

            // Invalidate session notes cache to ensure fresh data on next load
            const studentId = sessionData?.studentId || user?.studentId || user?.id;
            if (studentId) {
                await queryClient.invalidateQueries({
                    queryKey: ['sessionNotes', currentUser?.uid, typeOfUser, studentId],
                    refetchType: 'all',
                });
            }

            // On successful save, clear session data and close the dialog
            clearSessionData(sessionId);
            closeSession(sessionId);
        } catch (error) {
            console.error('Error saving session:', error);
            toast.error(error.message || 'Failed to save session', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            // On error, reopen the dialog so user can fix issues
            maximizeSession(sessionId);
        } finally {
            setIsSavingSessionNotes(false);
            setIsSaving(sessionId, false);
        }
    }, [
        sessionId,
        session,
        sessionData,
        user,
        currentUser,
        typeOfUser,
        preRegisteredUsers,
        goalsDataEntryRef,
        quickTracksDataEntryRef,
        goalsDataCardRef,
        quickTracksDataCardRef,
        queryClient,
        setIsSaving,
        clearSessionData,
        closeSession,
        maximizeSession,
        isSavingSessionNotes,
        setIsSavingSessionNotes,
        studentGoalsData,
        studentBehaviorData,
    ]);

    return {
        handleSaveDraft,
        handleSaveSession,
    };
};

